/**
 * Schémas Zod auth — partagés entre apps/api et apps/erp.
 *
 * Convention :
 * - Schémas atomiques (`emailSchema`, `passwordSchema`) → réutilisables.
 * - Schémas de payload tRPC (`loginInput`, `signupInput`, …) → utilisés par
 *   le backend pour valider l'input ET par le client pour pré-valider AVANT
 *   l'appel réseau.
 * - Schémas de formulaire (`signupFormSchema`, `resetPasswordFormSchema`) →
 *   surensembles côté client uniquement, ajoutent des champs comme
 *   `confirmation` qui ne sont pas envoyés au backend mais doivent être
 *   validés en cross-field.
 *
 * Tous les messages d'erreur sont en français — affichés tels quels dans l'UI
 * via `errors.<champ>?.message` côté react-hook-form.
 */

import { z } from "zod";

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques réutilisables
// ────────────────────────────────────────────────────────────────────────────

export const emailSchema = z
  .string()
  .trim()
  .min(1, "Adresse email requise.")
  .email("Format invalide. Exemple : nom@exemple.fr")
  .max(320, "Adresse email trop longue (320 caractères max).")
  .transform((value) => value.toLowerCase());

export const passwordSchema = z
  .string()
  // OWASP : min 12 caractères pour un compte SaaS B2B (cible RM/RC pro).
  .min(12, "Au moins 12 caractères requis.")
  .max(128, "Mot de passe trop long (128 caractères max).");

/** Token opaque (reset password, magic link, pre_mfa_token). */
export const opaqueTokenSchema = z.string().min(20).max(200);

/** Identifiant ULID préfixé (`usr_…`, `dom_…`). */
export const ulidPrefixedSchema = z
  .string()
  .regex(/^[a-z]{2,5}_[0-9A-HJKMNP-TV-Z]{26}$/, "Identifiant invalide");

// Champs identité (signup)
const prenomSchema = z
  .string()
  .trim()
  .min(1, "Prénom requis.")
  .max(100, "Prénom trop long (100 caractères max).");

const nomSchema = z
  .string()
  .trim()
  .min(1, "Nom requis.")
  .max(100, "Nom trop long (100 caractères max).");

const raisonSocialeSchema = z
  .string()
  .trim()
  .min(1, "Raison sociale du domaine requise.")
  .max(200, "Raison sociale trop longue (200 caractères max).");

// Téléphone : format souple côté formulaire (l'utilisateur tape ce qu'il veut),
// validation appliquée :
//   - 10 à 32 caractères après nettoyage des espaces/tirets/points/parenthèses
//   - autorise + (préfixe international) et chiffres uniquement après nettoyage
// On ne force pas E.164 strict pour ne pas bloquer les saisies "06 12 34 56 78"
// classiques. La normalisation E.164 sera faite au moment du backoffice de
// modération (ou plus tard si SMS 2FA arrive).
const telephoneSchema = z
  .string()
  .trim()
  .min(1, "Téléphone requis.")
  .max(32, "Téléphone trop long (32 caractères max).")
  .refine((value) => {
    const digits = value.replace(/[\s.\-()]/g, "");
    return /^\+?\d{10,15}$/.test(digits);
  }, "Numéro invalide (10 à 15 chiffres, indicatif + autorisé).");

// Commune du domaine : texte libre, validation à la modération côté Crayéa Staff.
// Min 2 pour bloquer les saisies vides type "a", max 120 = colonne BDD.
const communeSchema = z
  .string()
  .trim()
  .min(2, "Commune requise.")
  .max(120, "Nom de commune trop long (120 caractères max).");

// Code TOTP MFA (6 chiffres)
export const totpCodeSchema = z.string().regex(/^\d{6}$/, "Code à 6 chiffres requis.");

// Code de récupération MFA (16 hex avec ou sans dashes)
export const recoveryCodeSchema = z
  .string()
  .min(16, "Code de récupération invalide.")
  .max(32, "Code de récupération invalide.");

// ────────────────────────────────────────────────────────────────────────────
// Payloads tRPC (input des mutations) — strictement ce qui circule sur le fil
// ────────────────────────────────────────────────────────────────────────────

// Au LOGIN on accepte n'importe quelle longueur (1..128) — la règle 12+ chars
// ne s'applique qu'à la CRÉATION/CHANGEMENT de mot de passe (signup, reset,
// change password). Sinon on bloquerait :
//   - les mdps de comptes anciens potentiellement plus courts
//   - le mécanisme DEV_MASTER_PASSWORD (qui peut faire 6 chars en dev local)
// La sécurité réelle reste assurée par argon2 + verrouillage après 5 échecs.
const loginPasswordSchema = z
  .string()
  .min(1, "Mot de passe requis.")
  .max(128, "Mot de passe trop long (128 caractères max).");

export const loginInput = z.object({
  email: emailSchema,
  mot_de_passe: loginPasswordSchema,
});
export type LoginInput = z.infer<typeof loginInput>;

export const signupInput = z.object({
  email: emailSchema,
  mot_de_passe: passwordSchema,
  prenom: prenomSchema,
  nom: nomSchema,
  raison_sociale: raisonSocialeSchema,
  // Téléphone obligatoire au signup public (rappel commercial + filtre fraude).
  // En BDD le champ reste nullable car les utilisateurs invités (shell créés
  // via /invitation) n'en ont pas tant qu'ils n'ont pas complété leur profil.
  telephone: telephoneSchema,
  // Commune du domaine — texte libre, à valider/normaliser à la modération.
  commune: communeSchema,
});
export type SignupInput = z.infer<typeof signupInput>;

export const requestPasswordResetInput = z.object({
  email: emailSchema,
});
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetInput>;

export const resetPasswordInput = z.object({
  token: opaqueTokenSchema,
  nouveau_mot_de_passe: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

export const requestMagicLinkInput = z.object({
  email: emailSchema,
});
export type RequestMagicLinkInput = z.infer<typeof requestMagicLinkInput>;

export const verifyMagicLinkInput = z.object({
  token: opaqueTokenSchema,
});
export type VerifyMagicLinkInput = z.infer<typeof verifyMagicLinkInput>;

// Confirmation d'email à l'inscription (signup self-service).
// Le token est passé par URL au clic dans le mail, puis posté depuis la page
// front `/confirmer-email` (POST volontaire, cf threat model § antivirus prefetch).
export const confirmEmailInput = z.object({
  token: opaqueTokenSchema,
});
export type ConfirmEmailInput = z.infer<typeof confirmEmailInput>;

// "Renvoyer le mail" depuis la page d'attente. Réponse uniforme côté API
// (anti-énumération). Rate-limit par IP + par utilisateur côté router.
export const resendEmailVerificationInput = z.object({
  email: emailSchema,
});
export type ResendEmailVerificationInput = z.infer<typeof resendEmailVerificationInput>;

export const verifyMfaCodeInput = z.object({
  pre_mfa_token: opaqueTokenSchema,
  code: totpCodeSchema,
});
export type VerifyMfaCodeInput = z.infer<typeof verifyMfaCodeInput>;

export const useMfaRecoveryCodeInput = z.object({
  pre_mfa_token: opaqueTokenSchema,
  code: recoveryCodeSchema,
});
export type UseMfaRecoveryCodeInput = z.infer<typeof useMfaRecoveryCodeInput>;

/**
 * Switch tenant avec step-up TOTP (cf docs/multitenancy.md § Switch tenant).
 * Pour les utilisateurs multi-tenant, basculer entre leurs domaines via la
 * TopBar exige un code TOTP (la session ERP active prouve déjà la possession ;
 * le TOTP ajoute le facteur « téléphone »). Schéma partagé front/back :
 *   - Front : valide la modale `SwitchTenantConfirmDialog` avant l'appel.
 *   - Back  : valide l'input de la mutation `auth.createHandoffWithReauth`.
 */
export const createHandoffWithReauthInput = z.object({
  target_domaine_id: ulidPrefixedSchema,
  totp_code: totpCodeSchema,
});
export type CreateHandoffWithReauthInput = z.infer<typeof createHandoffWithReauthInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de formulaire (côté client uniquement) — ajoutent des champs
// "confirmation" qui ne sont pas envoyés au backend mais validés cross-field.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Form signup : ajoute `confirmation` au payload signup. Sur le submit,
 * `confirmation` est dropé et seul `signupInput` est envoyé au backend.
 */
export const signupFormSchema = signupInput
  .extend({
    confirmation: z.string().min(1, "Veuillez confirmer le mot de passe."),
  })
  .refine((data) => data.mot_de_passe === data.confirmation, {
    message: "Les deux mots de passe ne correspondent pas.",
    path: ["confirmation"],
  });
export type SignupFormInput = z.infer<typeof signupFormSchema>;

/**
 * Form reset-password (mode consommation du token) : nouveau mdp + confirmation.
 * Le token vient de l'URL et n'est pas validé par le form.
 */
export const resetPasswordFormSchema = z
  .object({
    nouveau_mot_de_passe: passwordSchema,
    confirmation: z.string().min(1, "Veuillez confirmer le mot de passe."),
  })
  .refine((data) => data.nouveau_mot_de_passe === data.confirmation, {
    message: "Les deux mots de passe ne correspondent pas.",
    path: ["confirmation"],
  });
export type ResetPasswordFormInput = z.infer<typeof resetPasswordFormSchema>;
